import { useEffect, useState } from 'react';
import { classNames } from '~/utils/classNames';
import {
  apiDeleteMemory,
  apiDeleteProjectMemory,
  apiListMemories,
  apiListProjectMemories,
  apiUpdateMemory,
  apiUpdateProjectMemory,
  type ProjectMemory,
  type UserMemory,
} from '~/lib/api/memory';

/** The subset both scopes share — everything the row UI actually renders. */
type MemoryRowData = {
  id: string;
  kind: string;
  key: string;
  content: string;
  source: string;
  status: 'active' | 'rejected';
  useCount: number;
};

// Per-kind tinting as literal utility classes so UnoCSS extracts them statically
// (dynamic `bg-${tone}` strings would not be picked up). Covers both kind
// vocabularies — user (brand/preference/constraint/fact) and project
// (stack/decision/convention/gotcha/brand/fact).
const KIND_META: Record<string, { label: string; icon: string; chip: string; text: string }> = {
  brand: { label: 'Brand', icon: 'i-ph:palette', chip: 'bg-accent/10 border-accent/20', text: 'text-accent' },
  preference: {
    label: 'Preference',
    icon: 'i-ph:sliders-horizontal',
    chip: 'bg-info/10 border-info/20',
    text: 'text-info',
  },
  constraint: {
    label: 'Constraint',
    icon: 'i-ph:shield-check',
    chip: 'bg-warning/10 border-warning/20',
    text: 'text-warning',
  },
  stack: { label: 'Stack', icon: 'i-ph:stack', chip: 'bg-info/10 border-info/20', text: 'text-info' },
  decision: { label: 'Decision', icon: 'i-ph:git-branch', chip: 'bg-info/10 border-info/20', text: 'text-info' },
  convention: {
    label: 'Convention',
    icon: 'i-ph:ruler',
    chip: 'bg-success/10 border-success/20',
    text: 'text-success',
  },
  gotcha: { label: 'Gotcha', icon: 'i-ph:warning', chip: 'bg-warning/10 border-warning/20', text: 'text-warning' },
  fact: { label: 'Fact', icon: 'i-ph:info', chip: 'bg-muted/10 border-muted/20', text: 'text-muted' },
};

const SOURCE_META: Record<string, { label: string; icon: string }> = {
  extracted: { label: 'Learned from chat', icon: 'i-ph:chat-circle-text' },
  remember_tool: { label: 'Saved by agent', icon: 'i-ph:sparkle' },
  user: { label: 'Edited by you', icon: 'i-ph:pencil-simple' },
};

export function MemoryTab() {
  const [memories, setMemories] = useState<UserMemory[] | null>(null);
  const [projectMemories, setProjectMemories] = useState<ProjectMemory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    apiListMemories()
      .then(setMemories)
      .catch(() => setError('Could not load your memory. Please try again.'));

    // Loaded independently so one failing scope still shows the other.
    apiListProjectMemories()
      .then(setProjectMemories)
      .catch(() => setProjectMemories([]));
  }, []);

  async function handleDelete(id: string) {
    setBusyId(id);
    setError(null);

    try {
      await apiDeleteMemory(id);
      setMemories((rows) => rows?.filter((r) => r.id !== id) ?? null);
    } catch {
      setError('Could not delete that memory. Please try again.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleToggle(row: MemoryRowData) {
    const nextStatus = row.status === 'active' ? 'rejected' : 'active';
    setBusyId(row.id);
    setError(null);

    try {
      await apiUpdateMemory(row.id, { status: nextStatus });
      setMemories((rows) => rows?.map((r) => (r.id === row.id ? { ...r, status: nextStatus } : r)) ?? null);
    } catch {
      setError('Could not update that memory. Please try again.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleProjectDelete(id: string) {
    setBusyId(id);
    setError(null);

    try {
      await apiDeleteProjectMemory(id);
      setProjectMemories((rows) => rows?.filter((r) => r.id !== id) ?? null);
    } catch {
      setError('Could not delete that memory. Please try again.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleProjectToggle(row: MemoryRowData) {
    const nextStatus = row.status === 'active' ? 'rejected' : 'active';
    setBusyId(row.id);
    setError(null);

    try {
      await apiUpdateProjectMemory(row.id, { status: nextStatus });
      setProjectMemories((rows) => rows?.map((r) => (r.id === row.id ? { ...r, status: nextStatus } : r)) ?? null);
    } catch {
      setError('Could not update that memory. Please try again.');
    } finally {
      setBusyId(null);
    }
  }

  const active = memories?.filter((m) => m.status === 'active') ?? [];
  const paused = memories?.filter((m) => m.status !== 'active') ?? [];

  // Group by app, most recently touched app first. Rows arrive newest-first, so
  // first appearance is already the right order for the groups themselves.
  const byProject: { projectId: string; projectName: string; rows: ProjectMemory[] }[] = [];

  for (const row of projectMemories ?? []) {
    const group = byProject.find((g) => g.projectId === row.projectId);

    if (group) {
      group.rows.push(row);
    } else {
      byProject.push({ projectId: row.projectId, projectName: row.projectName, rows: [row] });
    }
  }

  const nothingAtAll = memories !== null && memories.length === 0 && byProject.length === 0;

  return (
    <div>
      {/* Header */}
      <h3 className="ui-serif m-0 mb-1.5 text-[24px]">Memory</h3>
      <p className="m-0 mb-5 max-w-[600px] text-[12.5px] leading-[1.55] text-muted">
        What Dunea has learned — about you, and about each app you've built. Active memories are applied to future
        builds. Pause or delete anything here and it stops being used immediately.
      </p>

      {/* Inline error */}
      {error && (
        <div className="mb-3.5 flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/[0.07] px-3 py-2.5 text-[12.5px] text-danger">
          <span className="i-ph:warning-circle text-[14px]" />
          {error}
        </div>
      )}

      {/* Loading */}
      {memories === null && !error && (
        <div className="rounded-xl border border-hairline bg-raised px-5 py-10 text-center text-[12.5px] text-muted">
          <span className="i-ph:spinner-gap mr-1.5 animate-spin text-[14px]" />
          Loading your memory…
        </div>
      )}

      {/* Empty state */}
      {nothingAtAll && (
        <div className="rounded-xl border border-hairline bg-raised px-6 py-10 text-center">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-accent/20 bg-accent/10">
            <span className="i-ph:brain text-[22px] text-accent" />
          </div>
          <div className="ui-serif mb-1.5 text-[17px] text-primary">Nothing remembered yet</div>
          <div className="mx-auto max-w-[380px] text-[12.5px] leading-[1.55] text-muted">
            As you build, Dunea learns durable facts — your brand colors, preferred stack, tone of voice — and applies
            them to future sessions. They'll show up here.
          </div>
        </div>
      )}

      {/* ── About you ─────────────────────────────────────────────── */}
      {(active.length > 0 || paused.length > 0) && (
        <ScopeHeading title="About you" hint="Applied to every app you build." icon="i-ph:user-circle" />
      )}

      {active.length > 0 && (
        <MemorySection
          title="Active"
          count={active.length}
          rows={active}
          busyId={busyId}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />
      )}

      {paused.length > 0 && (
        <MemorySection
          title="Paused"
          count={paused.length}
          description="Not applied to builds — re-enable or delete."
          rows={paused}
          busyId={busyId}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />
      )}

      {/* ── About your apps ───────────────────────────────────────── */}
      {byProject.length > 0 && (
        <>
          <ScopeHeading
            title="About your apps"
            hint="Applied only to the app it was learned in."
            icon="i-ph:squares-four"
            spaced
          />

          {byProject.map((group) => (
            <MemorySection
              key={group.projectId}
              title={group.projectName}
              count={group.rows.length}
              rows={group.rows}
              busyId={busyId}
              onToggle={handleProjectToggle}
              onDelete={handleProjectDelete}
              monoTitle
            />
          ))}
        </>
      )}
    </div>
  );
}

function ScopeHeading({ title, hint, icon, spaced }: { title: string; hint: string; icon: string; spaced?: boolean }) {
  return (
    <div
      className={classNames(
        'flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-hairline pb-2',
        spaced ? 'mt-8' : 'mt-6',
      )}
    >
      <span className={classNames(icon, 'text-[14px] text-secondary')} />
      <span className="text-[13px] font-medium text-primary">{title}</span>
      <span className="text-[11.5px] text-faint">{hint}</span>
    </div>
  );
}

function MemorySection({
  title,
  count,
  description,
  rows,
  busyId,
  onToggle,
  onDelete,
  monoTitle,
}: {
  title: string;
  count: number;
  description?: string;
  rows: MemoryRowData[];
  busyId: string | null;
  onToggle: (row: MemoryRowData) => void;
  onDelete: (id: string) => void;
  /** App names are user content — render them distinctly from our own labels. */
  monoTitle?: boolean;
}) {
  return (
    <div className="mt-5.5">
      <div className="mb-2.5 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <span className={monoTitle ? 'text-[12.5px] font-medium text-primary [overflow-wrap:anywhere]' : 'ui-caption'}>
          {title}
        </span>
        <span className="rounded-full border border-hairline bg-sunken px-1.75 py-px text-[11px] leading-normal tabular-nums text-muted">
          {count}
        </span>
        {description && <span className="text-[11.5px] text-muted">{description}</span>}
      </div>

      <div className="overflow-hidden rounded-xl border border-hairline bg-raised">
        {rows.map((row, i) => (
          <MemoryRow
            key={row.id}
            row={row}
            busy={busyId === row.id}
            onToggle={onToggle}
            onDelete={onDelete}
            first={i === 0}
          />
        ))}
      </div>
    </div>
  );
}

function MemoryRow({
  row,
  busy,
  onToggle,
  onDelete,
  first,
}: {
  row: MemoryRowData;
  busy: boolean;
  onToggle: (row: MemoryRowData) => void;
  onDelete: (id: string) => void;
  first: boolean;
}) {
  const isPaused = row.status !== 'active';
  const kindMeta = KIND_META[row.kind] ?? KIND_META.fact;
  const sourceMeta = SOURCE_META[row.source] ?? { label: row.source, icon: 'i-ph:circle' };

  return (
    <div
      className={classNames(
        'flex flex-wrap items-start gap-3 px-4 py-3.5 transition-opacity duration-100 sm:flex-nowrap',
        !first && 'border-t border-hairline',
        busy ? 'opacity-50' : isPaused ? 'opacity-70' : 'opacity-100',
      )}
    >
      {/* Kind icon chip */}
      <div
        className={classNames(
          'mt-px flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border',
          kindMeta.chip,
        )}
        title={kindMeta.label}
      >
        <span className={classNames(kindMeta.icon, 'text-[13px]', kindMeta.text)} />
      </div>

      <div className="min-w-0 flex-1">
        {/* Meta row: key + kind label */}
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <code className="rounded border border-hairline bg-sunken px-1.75 py-0.5 font-geist-mono text-[11px] tracking-[-0.01em] text-secondary">
            {row.key}
          </code>
          <span className={classNames('text-[9.5px] font-semibold uppercase tracking-[0.09em]', kindMeta.text)}>
            {kindMeta.label}
          </span>
          {isPaused && (
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.09em] text-faint">Paused</span>
          )}
        </div>

        {/* Content */}
        <div className="text-[13px] leading-normal tracking-[-0.005em] text-primary [overflow-wrap:anywhere]">
          {row.content}
        </div>

        {/* Footer meta: source + usage */}
        <div className="mt-2 flex items-center gap-2 text-[10.5px] text-faint">
          <span className="inline-flex items-center gap-1">
            <span className={classNames(sourceMeta.icon, 'text-[11px]')} />
            {sourceMeta.label}
          </span>
          {row.useCount > 0 && (
            <>
              <span className="h-0.5 w-0.5 rounded-full bg-faint" />
              <span className="inline-flex items-center gap-1">
                <span className="i-ph:chart-line-up text-[11px]" />
                <span className="tabular-nums">
                  Used {row.useCount} {row.useCount === 1 ? 'time' : 'times'}
                </span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Actions — on phones they drop under the content so the memory text keeps the row's width */}
      <div className="flex flex-shrink-0 basis-full gap-1 pl-10 sm:basis-auto sm:pl-0">
        <button
          disabled={busy}
          onClick={() => onToggle(row)}
          title={isPaused ? 'Apply this memory to future builds again' : 'Stop applying this memory'}
          className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-hairline bg-transparent px-2.5 py-1.5 text-[11.5px] text-secondary transition-colors duration-100 hover:bg-white/[0.04] disabled:cursor-default touch:min-h-10 touch:px-3.5"
        >
          <span className={classNames(isPaused ? 'i-ph:play' : 'i-ph:pause', 'text-[11px]')} />
          {isPaused ? 'Enable' : 'Pause'}
        </button>
        <button
          disabled={busy}
          onClick={() => onDelete(row.id)}
          title="Delete this memory permanently"
          className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-hairline bg-transparent px-2.5 py-1.5 text-[11.5px] text-danger transition-colors duration-100 hover:border-danger/25 hover:bg-danger/[0.08] disabled:cursor-default touch:min-h-10 touch:px-3.5"
        >
          <span className="i-ph:trash text-[11px]" />
          Delete
        </button>
      </div>
    </div>
  );
}
